import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BellIcon, UserGroupIcon, UserIcon } from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NotificationManager = () => {
  const { token } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);

  // Form per notifica individuale
  const [individualForm, setIndividualForm] = useState({
    customer_code: '',
    title: '',
    message: '',
    notification_type: 'general'
  });

  // Form per notifica broadcast
  const [broadcastForm, setBroadcastForm] = useState({
    title: '',
    message: ''
  });

  useEffect(() => {
    loadCustomers();
    testConnection();
  }, []);

  const testConnection = async () => {
    setTestingConnection(true);
    try {
      const response = await axios.post(
        `${API}/onesignal/test`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setConnectionStatus(response.data);
      toast.success('OneSignal connesso!');
    } catch (error) {
      console.error('Errore test connessione:', error);
      setConnectionStatus({ status: 'error', message: error.message });
      toast.error('Errore connessione OneSignal');
    } finally {
      setTestingConnection(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const response = await axios.get(`${API}/fidelity/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Errore caricamento clienti:', error);
    }
  };

  const sendIndividualNotification = async (e) => {
    e.preventDefault();
    
    if (!individualForm.customer_code || !individualForm.title || !individualForm.message) {
      toast.error('Compila tutti i campi');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/onesignal/send-notification`,
        null,
        {
          params: {
            customer_code: individualForm.customer_code,
            title: individualForm.title,
            message: individualForm.message,
            notification_type: individualForm.notification_type
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Notifica inviata con successo!');
      setIndividualForm({
        customer_code: '',
        title: '',
        message: '',
        notification_type: 'general'
      });
    } catch (error) {
      console.error('Errore invio notifica:', error);
      toast.error('Errore nell\'invio della notifica');
    } finally {
      setLoading(false);
    }
  };

  const sendTestNotification = async (customer_code) => {
    setLoading(true);
    try {
      await axios.post(
        `${API}/onesignal/send-notification`,
        null,
        {
          params: {
            customer_code: customer_code,
            title: 'Test Notifica',
            message: 'Questa è una notifica di test dal sistema Vip Card Lacinio',
            notification_type: 'general'
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Notifica di test inviata!');
    } catch (error) {
      console.error('Errore invio test:', error);
      toast.error('Errore nell\'invio del test');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Gestione Notifiche Push</h1>
        <p className="text-gray-600">Invia notifiche ai clienti della Vip Card</p>
      </div>

      {/* Connection Status */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BellIcon className="w-5 h-5" />
            Stato Connessione OneSignal
          </CardTitle>
        </CardHeader>
        <CardContent>
          {testingConnection ? (
            <p className="text-gray-600">Verifica in corso...</p>
          ) : connectionStatus ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Badge variant={connectionStatus.status === 'success' ? 'default' : 'destructive'}>
                  {connectionStatus.status === 'success' ? '✓ Connesso' : '✗ Errore'}
                </Badge>
                {connectionStatus.app_name && (
                  <span className="text-sm text-gray-600">
                    App: {connectionStatus.app_name}
                  </span>
                )}
              </div>
              {connectionStatus.app_id && (
                <p className="text-xs text-gray-500">
                  App ID: {connectionStatus.app_id}
                </p>
              )}
            </div>
          ) : (
            <Button onClick={testConnection} variant="outline">
              Test Connessione
            </Button>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Notifica Individuale */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserIcon className="w-5 h-5" />
              Notifica a Cliente Singolo
            </CardTitle>
            <CardDescription>
              Invia una notifica personalizzata a un cliente specifico
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={sendIndividualNotification} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="customer">Cliente</Label>
                <select
                  id="customer"
                  value={individualForm.customer_code}
                  onChange={(e) => setIndividualForm({...individualForm, customer_code: e.target.value})}
                  className="w-full p-2 border rounded-md"
                  required
                >
                  <option value="">Seleziona un cliente...</option>
                  {customers.map(customer => (
                    <option key={customer.customer_code} value={customer.customer_code}>
                      {customer.name} {customer.surname} ({customer.email})
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="title">Titolo</Label>
                <Input
                  id="title"
                  placeholder="es. Nuova Promozione!"
                  value={individualForm.title}
                  onChange={(e) => setIndividualForm({...individualForm, title: e.target.value})}
                  required
                  maxLength={50}
                />
                <span className="text-xs text-gray-500">
                  {individualForm.title.length}/50 caratteri
                </span>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Messaggio</Label>
                <textarea
                  id="message"
                  placeholder="es. Sconto del 20% su tutti i prodotti questo weekend!"
                  value={individualForm.message}
                  onChange={(e) => setIndividualForm({...individualForm, message: e.target.value})}
                  className="w-full p-2 border rounded-md min-h-[100px]"
                  required
                  maxLength={200}
                />
                <span className="text-xs text-gray-500">
                  {individualForm.message.length}/200 caratteri
                </span>
              </div>

              <Button type="submit" disabled={loading} className="w-full">
                {loading ? 'Invio in corso...' : 'Invia Notifica'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Test Rapidi */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserGroupIcon className="w-5 h-5" />
              Test Rapidi
            </CardTitle>
            <CardDescription>
              Invia notifiche di test ai clienti
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <p className="text-sm text-gray-600 mb-4">
                Invia una notifica di test a un cliente per verificare che abbia abilitato le notifiche.
              </p>
              
              {customers.length === 0 ? (
                <p className="text-sm text-gray-500">Nessun cliente registrato</p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {customers.slice(0, 10).map(customer => (
                    <div
                      key={customer.customer_code}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
                    >
                      <div>
                        <p className="font-medium text-sm">
                          {customer.name} {customer.surname}
                        </p>
                        <p className="text-xs text-gray-500">{customer.email}</p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => sendTestNotification(customer.customer_code)}
                        disabled={loading}
                      >
                        Test
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Notifiche Automatiche Info */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Notifiche Automatiche Attive</CardTitle>
          <CardDescription>
            Queste notifiche vengono inviate automaticamente dal sistema
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
              <span className="text-2xl">👋</span>
              <div>
                <p className="font-medium">Benvenuto</p>
                <p className="text-sm text-gray-600">
                  Inviata quando un cliente si registra per la prima volta
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
              <span className="text-2xl">🎉</span>
              <div>
                <p className="font-medium">Punti Accreditati</p>
                <p className="text-sm text-gray-600">
                  Inviata quando accrediti punti tramite Scanner QR
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificationManager;
